import { useGetList } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";

import type { Contact, ContactNote } from "../types";
import { AccountsTable } from "./AccountsTable";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardKpis } from "./DashboardKpis";
import { DashboardScopeBar } from "./DashboardScopeBar";
import { DashboardStepper } from "./DashboardStepper";
import { MyDay } from "./MyDay";
import { NeedsAttention } from "./NeedsAttention";
import { PipelineByStage } from "./PipelineByStage";
import { Welcome } from "./Welcome";

const isDemo = import.meta.env.VITE_IS_DEMO === "true";

/**
 * The layout of the dashboard, and nothing else: every widget below owns its
 * own queries and its own scope. The only data read here is what decides
 * between the onboarding stepper and the real dashboard.
 */
export const Dashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  // Rendering nothing while these resolve left the home screen blank for as
  // long as the slowest count took, which read as a broken app.
  if (isPendingContact || isPendingContactNotes) {
    return <DashboardSkeleton />;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="flex flex-col gap-6 mt-1">
      <DashboardScopeBar />
      <DashboardKpis />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7">
          <AccountsTable />
        </div>
        <div className="md:col-span-5 flex flex-col gap-6">
          {isDemo ? <Welcome /> : null}
          <MyDay />
          <PipelineByStage />
          <NeedsAttention />
        </div>
      </div>
      {/* History, not work: last on the page and folded away. */}
      <DashboardActivityLog />
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="flex flex-col gap-6 mt-1">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-[58px] w-full" />
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-7 flex flex-col gap-2">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
      <div className="md:col-span-5 flex flex-col gap-6">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  </div>
);
